import { useState, useCallback } from 'react';
import { apiService, ChatFeedbackRequest, ChatMessage, ChatResponse, Citation, FeedbackValue } from '../utils/api';
import { v4 as uuidv4 } from 'uuid';

interface FeedbackState {
  value?: FeedbackValue;
  comment?: string;
  draftComment?: string;
  isEditorOpen?: boolean;
  isSaving?: boolean;
  error?: string | null;
}

interface ChatHistory {
  id: string;
  message: string;
  response: string;
  timestamp: string;
  responseId?: string;
  sources?: Citation[];
  isLoading?: boolean;
  feedback?: FeedbackState;
}

export const useChat = (userId?: string) => {
  const [sessionId, setSessionId] = useState<string>('');
  const [chatHistory, setChatHistory] = useState<ChatHistory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initializeChat = useCallback(async () => {
    try {
      setError(null);
      const welcome = await apiService.getWelcomeMessage();
      const newSessionId = uuidv4();
      
      setSessionId(newSessionId);
      setChatHistory([{
        id: uuidv4(),
        message: '',
        response: welcome.message,
        timestamp: welcome.timestamp,
      }]);
    } catch (err: any) {
      setError('Failed to initialize chat');
      console.error('Chat initialization error:', err);
    }
  }, []);

  const sendMessage = useCallback(async (message: string) => {
    if (!message.trim()) return;

    const messageId = uuidv4();
    const currentSessionId = sessionId || uuidv4();
    
    // Add user message to history with loading state
    const newChatItem: ChatHistory = {
      id: messageId,
      message: message.trim(),
      response: '',
      timestamp: new Date().toISOString(),
      isLoading: true
    };

    setChatHistory(prev => [...prev, newChatItem]);
    setIsLoading(true);
    setError(null);

    try {
      const chatMessage: ChatMessage = {
        message: message.trim(),
        session_id: currentSessionId,
        user_id: userId
      };

      const response: ChatResponse = await apiService.sendChatMessage(chatMessage);

      // Update the chat history with the response
      setChatHistory(prev =>
        prev.map(item =>
          item.id === messageId
            ? {
                ...item,
                response: response.response,
                timestamp: response.timestamp,
                responseId: response.response_id || undefined,
                sources: response.metadata?.sources || [],
                isLoading: false
              }
            : item
        )
      );

      // Update session ID if it was generated
      if (!sessionId) {
        setSessionId(response.session_id);
      }

    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to send message');
      
      // Update chat history to show error
      setChatHistory(prev => 
        prev.map(item => 
          item.id === messageId 
            ? { 
                ...item, 
                response: 'Sorry, I encountered an error. Please try again.', 
                isLoading: false 
              }
            : item
        )
      );
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, userId]);

  const clearChat = useCallback(async () => {
    try {
      if (sessionId) {
        await apiService.deleteChatSession(sessionId);
      }
    } catch (err) {
      console.warn('Failed to delete session:', err);
    } finally {
      setChatHistory([]);
      setSessionId('');
      setError(null);
      // Reinitialize with welcome message
      await initializeChat();
    }
  }, [sessionId, initializeChat]);

  const submitFeedback = useCallback(async (chatItemId: string, payload: ChatFeedbackRequest) => {
    const targetItem = chatHistory.find((item) => item.id === chatItemId);
    if (!targetItem?.responseId) {
      return;
    }
    const previousValue = targetItem.feedback?.value;
    const previousComment = targetItem.feedback?.comment;
    const previousDraftComment = targetItem.feedback?.draftComment ?? targetItem.feedback?.comment ?? '';

    setChatHistory((prev) =>
      prev.map((item) =>
        item.id === chatItemId
          ? {
              ...item,
              feedback: {
                value: payload.value,
                comment: item.feedback?.comment,
                draftComment: payload.comment ?? item.feedback?.draftComment ?? item.feedback?.comment ?? '',
                isEditorOpen: true,
                isSaving: true,
                error: null,
              },
            }
          : item
      )
    );

    try {
      const savedFeedback = await apiService.submitChatFeedback(targetItem.responseId, payload);
      setChatHistory((prev) =>
        prev.map((item) =>
          item.id === chatItemId
            ? {
                ...item,
                feedback: {
                  value: savedFeedback.value,
                  comment: savedFeedback.comment || '',
                  draftComment: savedFeedback.comment || '',
                  isEditorOpen: true,
                  isSaving: false,
                  error: null,
                },
              }
            : item
        )
      );
    } catch (err: any) {
      const message = err.response?.data?.detail || err.message || 'Failed to save feedback';
      setChatHistory((prev) =>
        prev.map((item) =>
          item.id === chatItemId
            ? {
                ...item,
                feedback: {
                  value: previousValue,
                  comment: previousComment,
                  draftComment: previousDraftComment,
                  isEditorOpen: Boolean(previousValue),
                  isSaving: false,
                  error: message,
                },
              }
            : item
        )
      );
    }
  }, [chatHistory]);

  const removeFeedback = useCallback(async (chatItemId: string) => {
    const targetItem = chatHistory.find((item) => item.id === chatItemId);
    if (!targetItem?.responseId) {
      return;
    }

    const previousFeedback = targetItem.feedback;

    setChatHistory((prev) =>
      prev.map((item) =>
        item.id === chatItemId
          ? {
              ...item,
              feedback: {
                ...item.feedback,
                isSaving: true,
                error: null,
              },
            }
          : item
      )
    );

    try {
      await apiService.deleteChatFeedback(targetItem.responseId);
      setChatHistory((prev) =>
        prev.map((item) =>
          item.id === chatItemId
            ? {
                ...item,
                feedback: undefined,
              }
            : item
        )
      );
    } catch (err: any) {
      const message = err.response?.data?.detail || err.message || 'Failed to remove feedback';
      setChatHistory((prev) =>
        prev.map((item) =>
          item.id === chatItemId
            ? {
                ...item,
                feedback: {
                  ...previousFeedback,
                  isSaving: false,
                  error: message,
                },
              }
            : item
        )
      );
    }
  }, [chatHistory]);

  const updateFeedbackDraft = useCallback((chatItemId: string, draftComment: string) => {
    setChatHistory((prev) =>
      prev.map((item) =>
        item.id === chatItemId
          ? {
              ...item,
              feedback: {
                value: item.feedback?.value,
                comment: item.feedback?.comment,
                draftComment,
                isEditorOpen: true,
                isSaving: item.feedback?.isSaving,
                error: item.feedback?.error ?? null,
              },
            }
          : item
      )
    );
  }, []);

  return {
    chatHistory,
    isLoading,
    error,
    sessionId,
    sendMessage,
    submitFeedback,
    removeFeedback,
    updateFeedbackDraft,
    clearChat,
    initializeChat
  };
};
